/**
 * useLinkedTranscript (KAN-639): switch the transcript modal to a linked
 * client card and back.
 *
 * The modal is keyed by an estimate id; for the linked client the latest
 * estimate with messages is picked (same rule as useClientTranscript). The
 * switch is dropped when the modal closes or the caller opens another dialog.
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientsApi } from '@/services/api/clients';
import { pickTranscriptEstimate } from './useClientTranscript';

export interface LinkedTranscriptTarget {
  clientId: string;
  estimateId: string;
  title: string;
}

interface LinkedClientRef {
  id: string;
  name: string;
  phone?: string;
}

export interface LinkedTranscriptState {
  /** Linked client shown instead of the caller's one, or null. */
  target: LinkedTranscriptTarget | null;
  /** Id of the linked client whose estimates are being loaded, if any. */
  loadingClientId: string | null;
  openLinked: (client: LinkedClientRef) => Promise<void>;
  back: () => void;
}

export function useLinkedTranscript(
  open: boolean,
  contactId: string | null
): LinkedTranscriptState {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<LinkedTranscriptTarget | null>(null);
  const [loadingClientId, setLoadingClientId] = useState<string | null>(null);

  useEffect(() => {
    setTarget(null);
  }, [open, contactId]);

  const openLinked = useCallback(
    async (client: LinkedClientRef) => {
      setLoadingClientId(client.id);
      try {
        const estimates = await queryClient.fetchQuery({
          queryKey: ['client-estimates', client.id],
          queryFn: () => clientsApi.getEstimates(client.id),
        });
        const estimate = pickTranscriptEstimate(estimates);
        if (!estimate) {
          toast.error('У связанного клиента нет переписки');
          return;
        }
        setTarget({
          clientId: client.id,
          estimateId: estimate.id,
          title: client.name || client.phone || 'Без имени',
        });
      } catch {
        toast.error('Не удалось открыть переписку');
      } finally {
        setLoadingClientId(null);
      }
    },
    [queryClient]
  );

  const back = useCallback(() => setTarget(null), []);

  return { target, loadingClientId, openLinked, back };
}
